import { useEffect } from "react";
import { faCaretLeft } from "@fortawesome/free-solid-svg-icons";
import NextLink from "next/link";
import Head from "next/head";
import { useRouter } from "next/compat/router";
import { Heading, Box, Link, Text, IconFa } from "@cruk/cruk-react-components";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { queryAsString } from "@fwa/src/utils/urlUtils";
import { fwsUrlTeamSlug } from "@fwa/src/services/teamService";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { getBrandForPage } from "@fwa/src/utils/pageUtils";

import {
  fundraisingPageListItemsTypes,
  TeamMembers,
} from "@fwa/src/components/TeamMembers";
import { ErrorPageWrapper } from "@fwa/src/components/ErrorPageWrapper";

import sharedStyles from "@fwa/src/components/styles.module.css";

import { type BrandNameType, type TeamPageType } from "@fwa/src/types";

export const TeamMembersPage = ({ edit = false }: { edit: boolean }) => {
  const router = useRouter();
  const [fundraiserState] = useFundraiserContext();
  const { fundraiser, loggedInStatus } = fundraiserState;
  const { slug } = router?.query || {};
  const slugString = queryAsString(slug);
  const urlOrNull = slugString.length
    ? fwsUrlTeamSlug({ slug: slugString })
    : null;
  // if url is null SWR does not fetch. Fetching without correct teamSlug will error with 404
  const { data: teamPage, error } = useSwrGet<TeamPageType>(urlOrNull);
  const isOwner =
    fundraiser &&
    teamPage &&
    fundraiser.uniqueId === teamPage?.teamLeaderPage.fundraiser.uniqueId;
  const accessError =
    loggedInStatus !== "unknown" && edit && teamPage && !isOwner
      ? {
          status: fundraiser ? 401 : 403,
          message: "Access denied",
          name: `not leader ${teamPage?.teamLeaderPage?.fundraiser.uniqueId}`,
        }
      : undefined;

  const themeName = teamPage
    ? teamPage?.theme || getBrandForPage(teamPage)
    : null;
  const [brandName, setBrandName] = useBrandContext();

  useEffect(() => {
    if (!!themeName && themeName !== brandName) {
      setBrandName(themeName as BrandNameType);
    }
  }, [themeName, brandName, setBrandName]);

  return (
    <ErrorPageWrapper error={error || accessError}>
      {teamPage ? (
        <>
          <Head>
            <title>{`${teamPage.title} ${edit ? "edit " : ""}members`}</title>
          </Head>
          <div className={sharedStyles["content-max-width"]}>
            <Box backgroundColor="background-light" padding="s" marginTop="m">
              <NextLink
                href={`/team${teamPage.url.slice(teamPage.url.lastIndexOf("/"))}`}
                data-cta-type="link-back-to-team"
              >
                <Link appearance="secondary" as="span">
                  <IconFa faIcon={faCaretLeft} size="s" />
                  Back to team page
                </Link>
              </NextLink>
            </Box>
            <Box backgroundColor="background-light" padding="s" marginTop="m">
              <Heading h5 as="p" marginBottom="xxs">
                Donate to a team member
              </Heading>
              <Text>Choose a team member to donate to below.</Text>
            </Box>

            {/* members block */}
            <Box
              backgroundColor="background-light"
              padding="s"
              marginBottom="s"
            >
              <Heading h1 textSize="xxl" overflowWrap="break-word">
                {edit ? "Edit " : ""}
                {teamPage.title} members
              </Heading>
              <TeamMembers
                teamId={teamPage.uniqueId}
                itemsPerPage={12}
                listItemType={
                  edit
                    ? fundraisingPageListItemsTypes.edit
                    : fundraisingPageListItemsTypes.donate
                }
              />
            </Box>
          </div>
        </>
      ) : null}
    </ErrorPageWrapper>
  );
};

export default TeamMembersPage;
